import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/lib/i18n";
import PublicLayout from "@/components/PublicLayout";
import AppShell, { RequireAuth } from "@/components/AppShell";
import { Home, Marketing, Pricing, Contact, Legal } from "@/pages/public/Public";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/public/Auth";
import Dashboard from "@/pages/app/Dashboard";
import TalentSearch from "@/pages/app/TalentSearch";
import AIDiscovery from "@/pages/app/AIDiscovery";
import Pool from "@/pages/app/Pool";
import { SavedSearches, TalentLists } from "@/pages/app/Workspace";
import Messages from "@/pages/app/Messages";
import { Projects, ProjectBoard } from "@/pages/app/Projects";
import { Imports, Credits, Billing, BillingSuccess, Team, SettingsPage } from "@/pages/app/Account";
import CandidatePortal from "@/pages/candidate/Portal";
import AdminConsole from "@/pages/admin/Admin";
import { tk } from "@/i18n";
import { PendingApproval, VerifyEmail } from "@/components/Approval";
import { IdleLogout } from "@/components/IdleLogout";

export const DOC_TITLE = tk("TOPTALENT.IO — Find the talent others can't.");

if (localStorage.getItem("tt_theme") === "dark") document.documentElement.classList.add("dark");

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/features" element={<Marketing page="features" />} />
              <Route path="/ai-search" element={<Marketing page="ai-search" />} />
              <Route path="/recruiters" element={<Marketing page="recruiters" />} />
              <Route path="/candidates" element={<Marketing page="candidates" />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Legal kind="privacy" />} />
              <Route path="/terms" element={<Legal kind="terms" />} />
            </Route>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/pending" element={<PendingApproval />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/app" element={<RequireAuth type="recruiter"><AppShell /></RequireAuth>}>
              <Route index element={<Dashboard />} />
              <Route path="search" element={<TalentSearch />} />
              <Route path="discovery" element={<AIDiscovery />} />
              <Route path="pool" element={<Pool />} />
              <Route path="saved" element={<SavedSearches />} />
              <Route path="lists" element={<TalentLists />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:id" element={<ProjectBoard />} />
              <Route path="messages" element={<Messages />} />
              <Route path="imports" element={<Imports />} />
              <Route path="credits" element={<Credits />} />
              <Route path="billing" element={<Billing />} />
              <Route path="billing/success" element={<BillingSuccess />} />
              <Route path="team" element={<Team />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            <Route path="/me/*" element={<RequireAuth type="candidate"><CandidatePortal /></RequireAuth>} />
            <Route path="/admin/*" element={<RequireAuth admin><AdminConsole /></RequireAuth>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <IdleLogout />
        <Toaster position="bottom-right" />
      </AuthProvider>
    </I18nProvider>
  );
}
